/**
 * Pinia Auth Store
 * 
 * 管理用戶認證狀態、登入登出邏輯以及用戶資料
 * 根據 Firebase Authentication 整合規格實作
 */

import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { i18n } from '../i18n'
import type { User } from 'firebase/auth'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  sendEmailVerification,
  onAuthStateChanged
} from 'firebase/auth'
import { auth, db } from '../config/firebase'
import { getUserProfile, updateUserProfile } from '../services/userService'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'

/**
 * UserProfile 介面定義
 * 
 * 用於存儲在 Firestore 中的完整用戶資料，包含業務邏輯相關的權限與組織資訊。
 * 與 Firebase Auth 的 User 物件分離，確保認證與業務資料的清楚職責分工。
 * 
 * 職責分工：
 * - Firebase Auth User: 負責身份認證（uid, email, emailVerified 等）
 * - UserProfile: 負責業務邏輯（權限、群組、組織等）
 */
interface UserProfile {
  /** Firebase 用戶唯一識別碼，與 Firebase Auth 保持一致 */
  uid: string
  /** 用戶電子郵件地址 */
  email: string
  /** 用戶顯示名稱，可選 */
  displayName?: string
  /** 用戶角色：owner(擁有者)、admin(管理員)、member(一般用戶) */
  role: 'owner' | 'admin' | 'member'
  /** 用戶所屬群組 ID 陣列，用於群組權限管理 */
  groupIds: string[]
  /** 用戶所屬組織 ID，可選，用於多租戶架構 */
  organizationId?: string
  /** 紀錄用戶使用過的功能，用於 onboarding 流程判斷 */
  experiences?: {
    superAdmin?: boolean
  }
  /** 平台管理員標記，由 Firebase console 手動設定 */
  isPlatformAdmin?: boolean
}

interface AuthResult {
  success: boolean
  error?: string
}

/**
 * 錯誤代碼對應的翻譯鍵值
 */
const getErrorMessageKey = (errorCode: string): string => {
  const errorMapping: Record<string, string> = {
    'auth/user-not-found': 'auth.errors.userNotFound',
    'auth/wrong-password': 'auth.errors.wrongPassword',
    'auth/email-already-in-use': 'auth.errors.emailAlreadyInUse',
    'auth/weak-password': 'auth.errors.weakPassword',
    'auth/invalid-email': 'auth.errors.invalidEmail',
    'auth/too-many-requests': 'auth.errors.tooManyRequests',
    'auth/network-request-failed': 'auth.errors.networkError',
    'auth/invalid-credential': 'auth.errors.invalidCredential',
    'auth/operation-not-allowed': 'auth.errors.operationNotAllowed',
    'auth/requires-recent-login': 'auth.errors.loginFailed',
    'auth/user-disabled': 'auth.errors.userDisabled',
    'auth/popup-closed-by-user': 'auth.errors.popupClosedByUser',
    'auth/popup-blocked': 'auth.errors.popupBlocked'
  }

  return errorMapping[errorCode] || 'auth.errors.loginFailed'
}

export const useAuthStore = defineStore('auth', () => {
  /**
   * 狀態管理
   *
   * 採用雙重狀態設計：
   * 1. user: Firebase Auth 認證狀態
   * 2. userProfile: Firestore 業務資料
   */

  /** Firebase Auth 用戶物件，包含認證相關資訊 (uid, email, emailVerified 等) */
  const user = ref<User | null>(null)

  /** 完整用戶資料，包含角色、群組等業務邏輯資訊，存儲於 Firestore */
  const userProfile = ref<UserProfile | null>(null)

  /** 載入狀態，用於顯示 loading 動畫 */
  const loading = ref(false)

  /** 錯誤訊息，用於顯示操作失敗的原因 */
  const error = ref<string | null>(null)

  /** i18n 翻譯函數，使用共享實例避免在非 setup 環境呼叫 useI18n() */
  const getTranslation = () => i18n.global.t
  
  /**
   * 計算屬性 (Computed Properties)
   * 
   * 提供響應式的衍生狀態，當依賴的狀態改變時會自動重新計算。
   * 這些 computed properties 讓組件可以直接使用衍生的用戶狀態，
   * 無需手動處理複雜的狀態邏輯或監聽狀態變化。
   * 
   * 使用方式：
   * 1. 在組件的 <script setup> 中：const authStore = useAuthStore()
   * 2. 直接存取屬性：authStore.isLoggedIn、authStore.userName 等
   * 3. 在 template 中使用：{{ authStore.userName }}
   * 4. 在 computed/watch 中使用：computed(() => authStore.isLoggedIn)
   */
  
  /** 
   * 登入狀態檢查
   * 
   * 當 Firebase Auth user 物件存在時返回 true，否則返回 false。
   * 這是最基礎的認證狀態判斷，用於決定是否顯示登入/登出相關 UI。
   * 
   * @returns {boolean} true 表示用戶已登入，false 表示未登入
   * 
   * @example
   * // 在組件中檢查登入狀態
   * const authStore = useAuthStore()
   * if (authStore.isLoggedIn) {
   *   // 執行需要登入的操作
   *   await loadUserData()
   * }
   * 
   * @example
   * // 在 template 中條件渲染
   * <div v-if="authStore.isLoggedIn">
   *   <h1>歡迎回來！</h1>
   *   <UserDashboard />
   * </div>
   * <div v-else>
   *   <LoginForm />
   * </div>
   * 
   * @example
   * // 在路由守衛中使用
   * router.beforeEach((to) => {
   *   const authStore = useAuthStore()
   *   if (to.meta.requiresAuth && !authStore.isLoggedIn) {
   *     return '/login'
   *   }
   * })
   */
  const isLoggedIn = computed(() => !!user.value)

  /** Firebase Auth 的 email 驗證狀態 */
  const emailVerified = computed(() => user.value?.emailVerified ?? false)
  
  /** 
   * 用戶顯示名稱
   * 
   * 按優先順序返回用戶的顯示名稱：
   * 1. Firestore userProfile.displayName (業務邏輯設定的名稱)
   * 2. Firebase Auth user.displayName (第三方登入提供的名稱)
   * 3. Firebase Auth user.email (最後備用選項)
   * 
   * 這個設計確保即使部分資料遺失，用戶界面仍能顯示有意義的識別資訊。
   * 
   * @returns {string} 用戶顯示名稱，絕不返回空字串
   * 
   * @example
   * // 在導航列顯示用戶名稱
   * <nav class="navbar">
   *   <div class="user-info">
   *     <img :src="userAvatar" alt="Avatar" />
   *     <span>{{ authStore.userName }}</span>
   *   </div>
   * </nav>
   * 
   * @example
   * // 在歡迎訊息中使用
   * <div class="welcome-message">
   *   <h2>歡迎回來，{{ authStore.userName }}！</h2>
   *   <p>今天是美好的一天</p>
   * </div>
   * 
   * @example
   * // 在用戶設定頁面作為預設值
   * <script setup>
   * const authStore = useAuthStore()
   * const displayName = ref(authStore.userName) // 預填目前名稱
   * </script>
   */
  const userName = computed(() => {
    if (userProfile.value?.displayName) return userProfile.value.displayName
    if (user.value?.displayName) return user.value.displayName
    return user.value?.email || ''
  })
  
  /** 
   * 用戶角色
   * 
   * 返回用戶在系統中的角色級別，用於權限控制和功能存取限制。
   * 角色層級：owner > admin > member
   * 
   * - owner: 系統擁有者，擁有所有權限
   * - admin: 管理員，可管理用戶和大部分系統設定
   * - member: 一般用戶，僅能存取基本功能
   * 
   * @returns {'owner' | 'admin' | 'member'} 用戶角色，預設為 'member'
   * 
   * @example
   * // 基於角色的權限檢查
   * const authStore = useAuthStore()
   * const canManageUsers = authStore.userRole === 'admin' || authStore.userRole === 'owner'
   * const canDeleteData = authStore.userRole === 'owner'
   * 
   * @example
   * // 在 template 中條件渲染管理功能
   * <div class="admin-panel" v-if="authStore.userRole !== 'member'">
   *   <button v-if="authStore.userRole === 'admin'">管理用戶</button>
   *   <button v-if="authStore.userRole === 'owner'">系統設定</button>
   * </div>
   * 
   * @example
   * // 建立角色檢查 composable
   * const usePermissions = () => {
   *   const authStore = useAuthStore()
   *   
   *   const canEdit = computed(() => authStore.userRole !== 'member')
   *   const canDelete = computed(() => authStore.userRole === 'owner')
   *   const isAdmin = computed(() => ['admin', 'owner'].includes(authStore.userRole))
   *   
   *   return { canEdit, canDelete, isAdmin }
   * }
   */
  const userRole = computed(() => userProfile.value?.role || 'member')
  
  /** 
   * 用戶所屬群組列表
   * 
   * 返回用戶所屬的群組 ID 陣列，用於細粒度的權限管理和內容過濾。
   * 群組權限比角色更靈活，允許跨職能的存取控制。
   * 
   * 常見使用場景：
   * - 部門權限管理 (sales-team, engineering-team)
   * - 專案存取控制 (project-alpha-members)
   * - 功能特性開關 (beta-testers, premium-users)
   * 
   * @returns {string[]} 用戶群組 ID 陣列，預設為空陣列
   * 
   * @example
   * // 檢查用戶是否屬於特定群組
   * const authStore = useAuthStore()
   * const isInSalesTeam = authStore.userGroups.includes('sales-team')
   * const canAccessBeta = authStore.userGroups.includes('beta-testers')
   * 
   * @example
   * // 過濾用戶可見的專案列表
   * const visibleProjects = computed(() => {
   *   return projects.value.filter(project => {
   *     // 檢查用戶群組是否與專案允許的群組有交集
   *     return authStore.userGroups.some(groupId => 
   *       project.allowedGroups.includes(groupId)
   *     )
   *   })
   * })
   * 
   * @example
   * // 建立群組檢查 composable
   * const useGroupAccess = () => {
   *   const authStore = useAuthStore()
   *   
   *   const hasGroupAccess = (requiredGroups: string[]) => {
   *     return requiredGroups.some(group => authStore.userGroups.includes(group))
   *   }
   *   
   *   const isInGroup = (groupId: string) => {
   *     return authStore.userGroups.includes(groupId)
   *   }
   *   
   *   return { hasGroupAccess, isInGroup }
   * }
   * 
   * @example
   * // 在路由守衛中使用群組權限
   * router.beforeEach((to) => {
   *   const authStore = useAuthStore()
   *   const requiredGroups = to.meta.requiredGroups as string[]
   *   
   *   if (requiredGroups && !authStore.userGroups.some(g => requiredGroups.includes(g))) {
   *     return '/403' // 無權限存取
   *   }
   * })
   */
  const userGroups = computed(() => userProfile.value?.groupIds || [])
  
  /** 
   * 用戶所屬組織 ID
   * 
   * 返回用戶所屬的組織識別碼，用於多租戶架構中的資料隔離和組織管理。
   * 在 B2B SaaS 應用中，不同組織的資料需要完全隔離。
   * 
   * 使用場景：
   * - API 請求時加入組織過濾條件
   * - 資料庫查詢時限制組織範圍
   * - 組織層級的功能設定和限制
   * 
   * @returns {string | undefined} 組織 ID，可能為 undefined（個人用戶或未設定組織）
   * 
   * @example
   * // 在 API 請求中使用組織 ID
   * const authStore = useAuthStore()
   * const fetchOrganizationData = async () => {
   *   if (!authStore.organizationId) {
   *     console.warn('用戶未屬於任何組織')
   *     return null
   *   }
   *   
   *   const response = await api.get(`/organizations/${authStore.organizationId}/data`)
   *   return response.data
   * }
   * 
   * @example
   * // 過濾組織相關資料
   * const organizationUsers = computed(() => {
   *   if (!authStore.organizationId) return []
   *   
   *   return users.value.filter(user => 
   *     user.organizationId === authStore.organizationId
   *   )
   * })
   * 
   * @example
   * // 在 Axios 請求攔截器中自動加入組織 ID
   * axios.interceptors.request.use(config => {
   *   const authStore = useAuthStore()
   *   
   *   if (authStore.organizationId && config.url?.includes('/api/')) {
   *     config.params = {
   *       ...config.params,
   *       orgId: authStore.organizationId
   *     }
   *   }
   *   
   *   return config
   * })
   * 
   * @example
   * // 建立組織權限檢查
   * const useOrganizationAccess = () => {
   *   const authStore = useAuthStore()
   *   
   *   const hasOrganization = computed(() => !!authStore.organizationId)
   *   const isOrganizationMember = (orgId: string) => authStore.organizationId === orgId
   *   
   *   return { hasOrganization, isOrganizationMember }
   * }
   */
  const organizationId = computed(() => userProfile.value?.organizationId)
  
  // 動作方法
  
  /**
   * 內部方法：直接建立用戶資料於 Firestore
   */
  const createUserProfileInternal = async (userData: {
    uid: string
    email: string
    displayName?: string | null
  }): Promise<UserProfile> => {
    try {
      console.log('🔧 直接建立 Firestore 用戶資料:', userData)
      
      const userProfile: UserProfile = {
        uid: userData.uid,
        email: userData.email,
        displayName: userData.displayName || null,
        role: 'member',
        groupIds: [],
        organizationId: null,
        disabled: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      
      const userDocRef = doc(db, 'users', userData.uid)
      await setDoc(userDocRef, userProfile)
      
      console.log('✅ Firestore 用戶資料建立成功')
      return {
        ...userProfile,
        createdAt: new Date(),
        updatedAt: new Date()
      }
    } catch (error: any) {
      console.error('建立用戶資料失敗:', error)
      throw new Error(`${getTranslation()('auth.errors.registrationFailed')}: ${error.message}`)
    }
  }
  
  /**
   * 用戶註冊
   */
  const register = async (email: string, password: string, displayName?: string): Promise<AuthResult> => {
    loading.value = true
    error.value = null
    
    try {
      // 1. 使用 Firebase Auth 註冊
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const firebaseUser = userCredential.user
      console.log('✅ Firebase User 創建成功:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified
      })

      // 2. 更新 Firebase Auth 的 displayName
      if (displayName) {
        await updateProfile(firebaseUser, { displayName })
      }

      // 2.5. 發送驗證信
      try {
        await sendEmailVerification(firebaseUser)
        console.log('✅ 驗證信已發送')
      } catch (emailError) {
        console.warn('⚠️ 發送驗證信失敗:', emailError)
        // 不阻止註冊流程
      }

      // 3. 建立 Firestore 用戶資料
      const createdProfile = await createUserProfileInternal({
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: displayName || null
      })

      // 4. 更新本機狀態 (同步雙重狀態)
      // 同時更新 Firebase Auth 狀態和 Firestore 業務資料，確保前端 UI 能立即響應
      user.value = firebaseUser
      userProfile.value = createdProfile || {
        // 備用預設值：當 Firestore 創建失敗時的容錯處理
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: displayName,
        role: 'member' as const, // 新用戶預設為一般用戶
        groupIds: [], // 新用戶預設無群組
        disabled: false // 新用戶預設未停用
      }
      
      console.log('✅ 註冊完成 - 本機狀態已更新:', {
        userUid: user.value?.uid,
        profileDisplayName: userProfile.value?.displayName,
        isLoggedIn: !!user.value
      })
      
      return { success: true }
    } catch (err: any) {
      console.error('註冊失敗:', err)
      const errorKey = getErrorMessageKey(err.code)
      error.value = getTranslation()(errorKey)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Email/Password 登入
   */
  const signIn = async (email: string, password: string): Promise<AuthResult> => {
    loading.value = true
    error.value = null
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const firebaseUser = userCredential.user

      console.log('✅ Firebase 登入成功:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified
      })

      // 獲取 Firestore 用戶資料
      const profile = await getUserProfile(firebaseUser.uid)
      
      // 更新 Pinia 狀態 (同步雙重狀態)
      // 登入成功後同步更新本機狀態，讓 computed properties 能立即反映登入狀態
      user.value = firebaseUser
      userProfile.value = profile || {
        // 備用預設值：當無法從 Firestore 獲取資料時的容錯處理
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName,
        role: 'member' as const, // 預設角色
        groupIds: [], // 預設無群組
        disabled: false // 預設未停用
      }

      console.log('✅ 登入完成 - 本機狀態已更新:', {
        userUid: user.value?.uid,
        profileDisplayName: userProfile.value?.displayName,
        isLoggedIn: !!user.value
      })

      return { success: true }
    } catch (err: any) {
      console.error('登入失敗:', err)
      const errorKey = getErrorMessageKey(err.code || err.message)
      error.value = getTranslation()(errorKey)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Google 登入
   */
  const signInWithGoogle = async (restrictDomain?: string): Promise<AuthResult> => {
    loading.value = true
    error.value = null
    
    try {
      const provider = new GoogleAuthProvider()
      
      if (restrictDomain) {
        provider.setCustomParameters({
          hd: restrictDomain // 限制特定網域
        })
      }

      const userCredential = await signInWithPopup(auth, provider)
      const firebaseUser = userCredential.user

      console.log('✅ Google 登入成功:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName
      })

      // 檢查是否為新用戶，若是則建立 Firestore 資料
      let profile = await getUserProfile(firebaseUser.uid)
      if (!profile) {
        console.log('🆕 新 Google 用戶，建立 Firestore 資料')
        profile = await createUserProfileInternal({
          uid: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName
        })
      }

      // 更新狀態 (同步雙重狀態)
      // Google 登入成功後同步更新本機狀態
      user.value = firebaseUser
      userProfile.value = profile || {
        // 備用預設值：適用於新 Google 用戶或 Firestore 讀取失敗的情況
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName,
        role: 'member' as const, // Google 用戶預設角色
        groupIds: [], // Google 用戶預設無群組
        disabled: false // Google 用戶預設未停用
      }

      console.log('✅ Google 登入完成 - 本機狀態已更新:', {
        userUid: user.value?.uid,
        profileDisplayName: userProfile.value?.displayName,
        isLoggedIn: !!user.value
      })

      return { success: true }
    } catch (err: any) {
      console.error('Google 登入失敗:', err)
      const errorKey = getErrorMessageKey(err.code)
      error.value = getTranslation()(errorKey)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * 登出
   */
  const signOutUser = async (): Promise<AuthResult> => {
    loading.value = true
    error.value = null
    
    try {
      // 1. Firebase Auth 登出
      await signOut(auth)
      console.log('✅ Firebase Auth 登出成功')
      
      // 2. 清除 Pinia 狀態 (重置雙重狀態)
      // 登出時清除所有本機狀態，確保不會有殘留的用戶資訊
      user.value = null
      userProfile.value = null
      
      // 3. 清除本機儲存 (如有)
      localStorage.removeItem('user-preferences')
      
      console.log('✅ 登出完成 - 本機狀態已清除並將跳轉到首頁')
      
      // 4. 導航到首頁
      window.location.href = '/'
      
      return { success: true }
    } catch (err: any) {
      console.error('登出失敗:', err)
      const errorKey = getErrorMessageKey(err.code)
      error.value = getTranslation()(errorKey)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * 初始化認證狀態監聽
   */
  const initializeAuth = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          // 用戶已登入，獲取完整用戶資料
          console.log('🔔 onAuthStateChanged: 用戶已登入', {
            uid: firebaseUser.uid,
            email: firebaseUser.email
          })
          
          try {
            // 獲取 Firestore 用戶資料並同步本機狀態
            const profile = await getUserProfile(firebaseUser.uid)
            
            // 檢查用戶是否被停用
            if (profile?.disabled) {
              console.warn('⛔ 用戶帳號已被停用，強制登出:', firebaseUser.email)
              await signOut(auth)
              
              // 清除本機狀態
              user.value = null
              userProfile.value = null
              
              // 顯示停用訊息並跳轉到首頁
              if (typeof window !== 'undefined') {
                alert(getTranslation()('auth.errors.userDisabled'))
                window.location.href = '/'
              }
              
              resolve(false)
              return
            }
            
            user.value = firebaseUser
            userProfile.value = profile || {
              // 備用預設值：當 Firestore 無法提供完整資料時的容錯處理
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName,
              role: 'member' as const, // 預設角色
              groupIds: [], // 預設無群組
              disabled: false // 預設未停用
            }
            
            console.log('✅ 認證狀態初始化完成 - 用戶已登入')
            resolve(true)
          } catch (err) {
            console.error('獲取用戶資料失敗:', err)
            // 如果無法獲取用戶資料，強制登出
            await signOutUser()
            resolve(false)
          }
        } else {
          // 用戶未登入，清除所有狀態
          console.log('🔔 onAuthStateChanged: 用戶未登入')
          user.value = null
          userProfile.value = null
          
          console.log('✅ 認證狀態初始化完成 - 用戶未登入')
          resolve(false)
        }
      })
      
      // 儲存取消訂閱函數供後續使用
      if (typeof window !== 'undefined') {
        ;(window as any).__authUnsubscribe = unsubscribe
      }
    })
  }

  /**
   * 刷新用戶資料
   */
  const refreshUserProfile = async (): Promise<void> => {
    if (!user.value) return
    
    try {
      const profile = await getUserProfile(user.value.uid)
      if (profile) {
        userProfile.value = profile
        console.log('✅ 用戶資料刷新成功')
      }
    } catch (err) {
      console.error('刷新用戶資料失敗:', err)
    }
  }

  /**
   * 更新用戶個人資料
   * @param profileData - 要更新的資料，例如 { displayName: '新名字' }
   */
  const updateProfile = async (profileData: Partial<Pick<UserProfile, 'displayName'>>): Promise<AuthResult> => {
    if (!user.value) {
      const message = getTranslation()('auth.errors.loginFailed');
      console.error(`❌ ${message}`);
      return { success: false, error: message };
    }

    loading.value = true;
    error.value = null;

    try {
      console.log('🔧 更新用戶資料:', profileData);
      
      // 1. 更新 Firebase Auth 的 Profile (如果需要)
      if (profileData.displayName && profileData.displayName !== user.value.displayName) {
        await updateProfile(user.value, { displayName: profileData.displayName });
        console.log('✅ Firebase Auth Profile 更新成功');
      }
      
      // 2. 更新 Firestore 的用戶資料
      await updateUserProfile(user.value.uid, profileData);

      // 3. 刷新本地用戶資料
      await refreshUserProfile();
      // 手動更新 auth user 物件以觸發響應
      if (auth.currentUser) {
        user.value = { ...auth.currentUser };
      }

      console.log('✅ 用戶資料更新流程完成');
      return { success: true };
    } catch (err: any) {
      console.error('更新用戶資料失敗:', err);
      const errorKey = getErrorMessageKey(err.code);
      error.value = getTranslation()(errorKey) || err.message;
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  };

  /**
   * 清除錯誤狀態
   */
  const clearError = (): void => {
    error.value = null
  }

  /** 重新寄送 email 驗證信 */
  const resendVerificationEmail = async (): Promise<void> => {
    if (user.value) {
      await sendEmailVerification(user.value)
    }
  }

  return {
    // 狀態
    user: readonly(user),
    userProfile: readonly(userProfile),
    loading: readonly(loading),
    error: readonly(error),

    // 計算屬性
    isLoggedIn,
    emailVerified,
    userName,
    userRole,
    userGroups,
    organizationId,

    // 方法
    register,
    signIn,
    signInWithGoogle,
    signOut: signOutUser,
    initializeAuth,
    refreshUserProfile,
    updateProfile,
    clearError,
    resendVerificationEmail
  }
})